import type { PromptResolve } from '@ur-solutions/react-components'
import { Icon, usePrompt } from '@ur-solutions/react-components'
import type { UseFormReturn } from '@ur-solutions/react-hooks'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Button } from '@/components_deprecated/Button'
import { Card as BaseCard } from '@/components_deprecated/Card'
import { Checkbox } from '@/components_deprecated/Checkbox'
import { FAIcon } from '@/components_deprecated/icons'
import { Input, NumberInput } from '@/components_deprecated/Input'
import { Select, SelectMultiple } from '@/components_deprecated/Select'
import { DepartmentSelect } from '@/components_deprecated/Select/DepartmentSelect'
import { SalaryCodeSelect } from '@/components_deprecated/Select/SalaryCodeSelect'
import { SelectMultipleDepartments } from '@/components_deprecated/Select/SelectMultipleDepartments'
import { TimePicker as BaseTimePicker } from '@/components_deprecated/TimePicker'
import clamp from 'lodash/clamp'
import uniqueId from 'lodash/uniqueId'
import { Module } from '@/modules/companies/consts'
import type {
  SalaryModuleConfigNode,
  SalaryWorkTimeNode,
} from '@/modules/companies/types.graphql'
import { SalaryType } from '@/modules/companies/types.graphql'
import React, { useEffect, useMemo } from 'react'
import { isMobileOnly } from 'react-device-detect'
import styled from 'styled-components'
import { useCompany, useConfirm } from '@/utils/hooks'
import { Field, Form } from '..'
import type { PeriodAddonForm } from './PeriodAddonPrompt'
import { PeriodPrompt } from './PeriodAddonPrompt'
import { PeriodAddonView } from './PeriodAddonView'
import { SelectMultipleSalaryCodes } from '@/components_deprecated/Select/SelectMultipleSalaryCodes'
import { H3 } from '@/components'
import { useHolidayAllowances } from './hooks'

const Card = styled(BaseCard)`
  margin-bottom: 1rem;
`

const TimePicker = styled(BaseTimePicker)`
  .--time-picker-inner-wrapper {
    justify-content: flex-start;

    input:last-of-type {
      width: 100%;
    }
  }
`
const PeriodField = styled(Field)`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  grid-template-rows: auto auto;
  column-gap: 1rem;

  label {
    grid-column: 1 / -1;
  }
  i {
    align-self: center;
  }

  ${props => props.theme.media.mobile} {
    gap: 0.5rem;
  }
`

const SpecialLabel = styled.label`
  border-left: 3px solid ${props => props.theme.colors.primary};
  padding-left: 0.5rem;
`

export interface SalarySettingsForm {
  salaryGroups: string[]
  userId: string
  dailyWorkLimit: number
  periodWorkLimit: number

  pauseDuration: Date
  pauseThreshold: Date
  countPaidPauseInTotalWork: boolean
  paidPauseThreshold: Date
  manuallyAddPaidPauseForThresholds: boolean

  salaryDateStart: number
  salaryDateEnd: number
  name: string
  usePeriodOvertime: boolean
  useSalaryDayPayment: boolean
  groupSalaryOnVehicle: boolean

  salaryType: keyof typeof SalaryType

  defaultSalaryCode: string | null
  salaryDayCode: string | null

  drivingSalaryCode: string | null
  otherWorkSalaryCode: string | null
  availabilitySalaryCode: string | null
  paidPauseSalaryCode: string | null

  dayOvertimeSalaryCode: string | null
  useWeekOvertime: boolean
  calculateWeekOvertimeInline: boolean
  weekOvertimeSalaryCode: string | null
  useMonthOvertime: boolean
  monthOvertimeSalaryCode: string | null
  periodAddons: PeriodAddonForm[]

  defaultDepartment: string | null
  availableDepartments: string[]

  availableAddons: string[]

  useAutomaticDietGeneration: boolean
  useAutomaticSalaryDayGeneration: boolean

  holidayAllowances: string[]
}

interface SalarySettingsFormProps {
  form: UseFormReturn<SalarySettingsForm>
  config: SalaryModuleConfigNode
  salaryWorkTime?: SalaryWorkTimeNode

  onSubmit: PromptResolve<SalarySettingsForm | null>
}

export const SalarySettingsFormView: React.FC<SalarySettingsFormProps> = ({
  form,
  config,
  onSubmit,
}) => {
  const translations = useTranslate({
    driving: 'common.driving',
    otherWork: 'common.other-work',
    availability: 'common.availability',

    pause: 'common.pause',
    overtime: 'common.overtime',
    dataDistribution: 'common.data-distribution',
    salaryCode: 'common.salary-code',
    salaryCodeDay: 'settings.salary-code-day',

    editSalarySettings: ['settings.edit-salary-settings-type', { module: '' }],
    createSalarySettings: ['settings.create-salary-settings', { module: '' }],
    countPaidPauseInTotalWork:
      'settings.timesheets.count-pause-as-part-of-total-work',

    useWeekOvertime: 'settings.use-week-overtime',
    useMonthOvertime: 'settings.use-month-overtime',

    dailyWorkLimit: 'settings.daily-work-limit',
    periodWorkLimit: 'settings.period-work-limit',
    pauseDuration: 'settings.pause-duration',
    pauseThreshold: 'settings.pause-threshold',
    manuallyAddPaidPauseForThresholds:
      'settings.manually-add-paid-pause-for-thresholds',
    paidPauseThreshold: 'settings.paid-pause-threshold',
    paidPauseSalaryCode: 'settings.paid-pause-salary-code',
    salaryDateStart: 'settings.salary-start-date',
    salaryDateEnd: 'settings.salary-end-date',
    salaryPeriod: 'settings.salary-period',
    name: 'common.name',
    groupSalaryOnVehicle: 'settings.group-salary-on-vehicle',
    usePeriodAddons: 'settings.use-period-addons',
    useSalaryDayPayment: 'settings.use-salary-day-payment',

    defaultSalaryCode: 'settings.default-salary-code',
    daySalaryCode: 'settings.day-salary-code',
    weekSalaryCode: 'settings.week-salary-code',
    weekOrMonthOvertime: 'settings.week-or-month-overtime',

    deleteOvertimePeriod: 'settings.delete-overtime-period',
    doYouWantToDeleteOvertimePeriod:
      'settings.do-you-want-to-delete-overtime-period',
    deletePeriodAddon: 'settings.delete-period-addon',
    doYouWantToDeletePeriodAddon: 'settings.do-you-want-to-delete-period-addon',

    delete: 'common.delete-alt',
    save: 'common.save',
    add: 'common.add',

    salaryGroups: 'salary.salary-groups',

    activities: 'common.activities',
    terminal: 'common.terminal',
    timesheets: 'common.timesheets',

    fixedSalary: 'settings.fixed-salary',
    hourlySalary: 'settings.hourly-salary',
    dailySalary: 'settings.daily-salary',

    salaryType: 'settings.salary-type',
    standardSettings: 'settings.standard-settings',
    salaryDayOtherSalaryCodeWarning:
      'settings.salary-day-other-salary-code-warning',
    weekOvertimeDelayedOvertimeWarning:
      'settings.week-overtime-delayed-overtime-warning',
    departments: 'common.departments',
    defaultDepartment: 'settings.default-department',
    availableDepartments: 'settings.available-departments',
    availableAddons: 'settings.available-addons',

    salaryTypeAndSalaryCode: 'settings.salary-type-and-salary-code',
    salaryCodeForActivityType: 'settings.salary-code-for-activity-type',

    dropdownFiltersAndStandardValues:
      'settings.dropdown-filters-and-standard-values',

    useAutomaticDietGeneration: 'settings.use-automatic-diet-generation',
    useAutomaticSalaryDayGeneration:
      'settings.use-automatic-salary-day-generation',

    calculateWeekOvertimeInline: 'settings.calculate-week-overtime-inline',
    holidayAllowances: 'common.holiday-allowances',

    validation: {
      driverPause: 'settings.validation.salary-driver-pause-length',
      invalid: 'common.invalid',
    },
  })

  const {
    formValues,
    formErrors: errors,
    formChangeHandler: handler,
    updateForm,
    formValid,
  } = form

  const addPrompt = usePrompt()
  const addConfirm = useConfirm()
  const company = useCompany()
  const holidayAllowances = useHolidayAllowances()

  const companysalaryGroups = useMemo(
    () => company?.salaryGroups ?? [],
    [company?.salaryGroups]
  )

  function updateDates() {
    return (salaryDateStart: number) => {
      const salaryDateEnd = clamp(
        salaryDateStart === 1 ? 31 : salaryDateStart - 1,
        1,
        31
      )
      updateForm({
        salaryDateStart,
        salaryDateEnd,
      })
    }
  }

  const salaryGroupSelectOptions = useMemo(
    () =>
      companysalaryGroups.map(salaryGroup => ({
        value: salaryGroup.id,
        label: salaryGroup.name,
      })) ?? [],
    [companysalaryGroups]
  )

  const holidayAllowanceSelectOptions = useMemo(
    () =>
      holidayAllowances.map(holidayAllowance => ({
        value: holidayAllowance.id,
        label: `(${holidayAllowance.salaryCode.name}) ${holidayAllowance.day}`,
      })) ?? [],
    [holidayAllowances]
  )

  async function addPeriodAddon() {
    const { data } = await addPrompt<PeriodAddonForm | null>(resolve => (
      <PeriodPrompt
        period={null}
        onSubmit={resolve}
        existingPeriods={form.formValues.periodAddons}
      />
    ))
    if (!data) return
    updateForm({
      periodAddons: [
        ...form.formValues.periodAddons,
        {
          id: uniqueId('overtime-period-'),
          start: data.start,
          end: data.end,
          salaryCode: data.salaryCode,
          salaryCodeName: data.salaryCodeName,
          monday: data.monday,
          tuesday: data.tuesday,
          wednesday: data.wednesday,
          thursday: data.thursday,
          friday: data.friday,
          saturday: data.saturday,
          sunday: data.sunday,
          excludeIfOvertime: data.excludeIfOvertime,
          override: data.override,
        },
      ],
    })
  }

  async function editPeriodAddon(periodAddon: PeriodAddonForm) {
    const { data } = await addPrompt<PeriodAddonForm | null>(resolve => (
      <PeriodPrompt
        period={periodAddon}
        onSubmit={resolve}
        existingPeriods={form.formValues.periodAddons}
      />
    ))
    if (!data) return
    updateForm({
      periodAddons: form.formValues.periodAddons.map(period =>
        period.id === periodAddon.id ? data : period
      ),
    })
  }

  async function deletePeriodAddon(periodAddon: PeriodAddonForm) {
    const { data } = await addConfirm(
      translations.doYouWantToDeletePeriodAddon,
      translations.deletePeriodAddon,
      {
        confirmText: translations.delete,
      }
    )
    if (!data) return
    updateForm({
      periodAddons: form.formValues.periodAddons.filter(
        period => period.id !== periodAddon.id
      ),
    })
  }

  useEffect(() => {
    if (
      !(
        config.name === Module.TIMESHEETS || config.name === Module.ACTIVITIES
      ) &&
      (formValues.usePeriodOvertime || formValues.periodAddons.length > 0)
    )
      updateForm({ usePeriodOvertime: false, periodAddons: [] })
  }, [
    config,
    formValues.periodAddons.length,
    formValues.usePeriodOvertime,
    updateForm,
  ])

  const salaryTypeOptions =
    config.name === Module.ACTIVITIES
      ? [
          {
            value: SalaryType.FIXED,
            label: translations.fixedSalary,
          },
          {
            value: SalaryType.HOUR,
            label: translations.hourlySalary,
          },
          {
            value: SalaryType.DAY,
            label: translations.dailySalary,
          },
        ]
      : [
          {
            value: SalaryType.FIXED,
            label: translations.fixedSalary,
          },
          {
            value: SalaryType.HOUR,
            label: translations.hourlySalary,
          },
        ]

  return (
    <Form
      buttons={[
        {
          text: translations.save,
          buttonProps: { disabled: !formValid },
          onClick: () => onSubmit(formValues),
        },
      ]}
    >
      <Card>
        <H3 className="mb-2">{translations.standardSettings}</H3>
        <Field>
          <label>{translations.name}</label>
          <Input
            fullWidth
            placeholder={translations.name}
            value={formValues.name}
            onChange={handler('name')}
          />
        </Field>

        {!formValues.userId && (
          <Field>
            <label>{translations.salaryGroups}</label>
            <SelectMultiple
              fullWidth
              selected={formValues.salaryGroups}
              options={salaryGroupSelectOptions}
              onChange={handler('salaryGroups')}
            />
          </Field>
        )}

        <PeriodField>
          <label>{translations.salaryPeriod}</label>

          <NumberInput
            value={formValues.salaryDateStart}
            min={1}
            max={31}
            onChange={updateDates()}
          />

          <Icon icon="arrow-right" color="gray6" size="1.2rem" />

          <NumberInput
            value={formValues.salaryDateEnd}
            error={errors.salaryDateStart}
            disabled
            min={1}
            max={31}
            color="gray6"
            onChange={() => void 0}
          />
        </PeriodField>
      </Card>
      <Card>
        <H3 className="mb-2">{translations.salaryType}</H3>
        <Field>
          <label>{translations.salaryType}</label>
          <Select
            fullWidth
            value={formValues.salaryType}
            options={salaryTypeOptions}
            onChange={value =>
              handler('salaryType')(value as keyof typeof SalaryType)
            }
          />
        </Field>

        {formValues.salaryType === SalaryType.DAY && (
          <Field>
            <label>{translations.salaryCodeDay}</label>
            <SalaryCodeSelect
              error={errors.salaryDayCode}
              fullWidth
              nullable
              value={formValues.salaryDayCode}
              onChange={handler('salaryDayCode')}
            />
          </Field>
        )}

        <Field>
          <label>{translations.defaultSalaryCode}</label>
          <SalaryCodeSelect
            error={errors.defaultSalaryCode}
            fullWidth
            nullable
            value={formValues.defaultSalaryCode}
            onChange={handler('defaultSalaryCode')}
          />
        </Field>

        {formValues.salaryType === SalaryType.DAY && (
          <Field>
            <SpecialLabel>
              {translations.salaryDayOtherSalaryCodeWarning}
            </SpecialLabel>
          </Field>
        )}
      </Card>

      <Card>
        <H3 className="mb-2">
          {translations.dropdownFiltersAndStandardValues}
        </H3>
        {(config.name === Module.TIMESHEETS ||
          config.name === Module.ACTIVITIES) && (
          <>
            <Field>
              <label>{translations.defaultDepartment}</label>
              <DepartmentSelect
                value={formValues.defaultDepartment}
                onChange={handler('defaultDepartment')}
                width="100%"
              />
            </Field>
            {config.name === Module.TIMESHEETS && (
              <Field>
                <label>{translations.availableDepartments}</label>
                <span>
                  <SelectMultipleDepartments
                    selected={formValues.availableDepartments}
                    onChange={handler('availableDepartments')}
                    width="100%"
                    grayed={false}
                  />
                </span>
              </Field>
            )}
          </>
        )}
        <Field>
          <label>{translations.availableAddons}</label>
          <SelectMultipleSalaryCodes
            selected={formValues.availableAddons}
            onChange={handler('availableAddons')}
            width="100%"
            grayed={false}
          />
        </Field>
      </Card>

      <Card>
        <H3 className="mb-2">{translations.daySalaryCode}</H3>
        <Field>
          <label>{translations.dailyWorkLimit}</label>
          <NumberInput
            value={formValues.dailyWorkLimit}
            min={0}
            max={24}
            onChange={handler('dailyWorkLimit')}
          />
        </Field>

        <Field>
          <label>{translations.defaultSalaryCode}</label>
          <SalaryCodeSelect
            error={errors.dayOvertimeSalaryCode}
            fullWidth
            nullable
            value={formValues.dayOvertimeSalaryCode}
            onChange={handler('dayOvertimeSalaryCode')}
          />
        </Field>
      </Card>

      <Card>
        <H3 className="mb-2">{translations.weekOrMonthOvertime}</H3>
        <Field>
          <label>{translations.useWeekOvertime}</label>
          <span>
            <Checkbox
              checked={formValues.useWeekOvertime}
              onChange={value => {
                if (value) {
                  updateForm({
                    useWeekOvertime: value,
                    useMonthOvertime: !value,
                    calculateWeekOvertimeInline: !value,
                  })
                } else {
                  updateForm({ useWeekOvertime: value })
                }
              }}
            />
          </span>
        </Field>

        {formValues.useWeekOvertime && (
          <>
            <Field>
              <SpecialLabel>
                {translations.weekOvertimeDelayedOvertimeWarning}
              </SpecialLabel>
            </Field>
            <Field>
              <label>{translations.calculateWeekOvertimeInline}</label>
              <span>
                <Checkbox
                  checked={formValues.calculateWeekOvertimeInline}
                  onChange={value => {
                    updateForm({
                      calculateWeekOvertimeInline: value,
                    })
                  }}
                />
              </span>
            </Field>
          </>
        )}

        <Field>
          <label>{translations.useMonthOvertime}</label>
          <span>
            <Checkbox
              checked={formValues.useMonthOvertime}
              onChange={value => {
                if (value) {
                  updateForm({
                    useWeekOvertime: !value,
                    useMonthOvertime: value,
                  })
                } else {
                  updateForm({ useMonthOvertime: value })
                }
              }}
            />
          </span>
        </Field>
        {(formValues.useWeekOvertime || formValues.useMonthOvertime) && (
          <>
            <Field>
              <label>{translations.salaryCode}</label>
              <SalaryCodeSelect
                error={errors.weekOvertimeSalaryCode}
                fullWidth
                nullable
                value={formValues.weekOvertimeSalaryCode}
                onChange={handler('weekOvertimeSalaryCode')}
              />
            </Field>
            <Field>
              <label>{translations.periodWorkLimit}</label>
              <NumberInput
                value={formValues.periodWorkLimit}
                min={0}
                max={24 * 7}
                onChange={handler('periodWorkLimit')}
              />
            </Field>
          </>
        )}
      </Card>
      <Card>
        <H3 className="mb-2">{translations.pause}</H3>
        {config.name === Module.ACTIVITIES && (
          <Field>
            <label>{translations.manuallyAddPaidPauseForThresholds}</label>
            <span>
              <Checkbox
                checked={formValues.manuallyAddPaidPauseForThresholds}
                onChange={handler('manuallyAddPaidPauseForThresholds')}
              />
            </span>
          </Field>
        )}

        <Field>
          <label>{translations.pauseDuration}</label>
          <TimePicker
            value={formValues.pauseDuration}
            error={errors.pauseDuration}
            width="100%"
            onChange={handler('pauseDuration')}
            disabled={
              !formValues.manuallyAddPaidPauseForThresholds &&
              config.name === Module.ACTIVITIES
            }
            grayed={
              !formValues.manuallyAddPaidPauseForThresholds &&
              config.name === Module.ACTIVITIES
            }
          />
        </Field>

        <Field>
          <label>{translations.pauseThreshold}</label>
          <TimePicker
            value={formValues.pauseThreshold}
            width="100%"
            onChange={handler('pauseThreshold')}
            disabled={
              !formValues.manuallyAddPaidPauseForThresholds &&
              config.name === Module.ACTIVITIES
            }
            grayed={!formValues.manuallyAddPaidPauseForThresholds}
          />
        </Field>

        {config.name === Module.ACTIVITIES && (
          <>
            <Field>
              <label>{translations.paidPauseThreshold}</label>
              <TimePicker
                value={formValues.paidPauseThreshold}
                width="100%"
                onChange={handler('paidPauseThreshold')}
                disabled={formValues.manuallyAddPaidPauseForThresholds}
                grayed={formValues.manuallyAddPaidPauseForThresholds}
              />
            </Field>
          </>
        )}
        <Field>
          <label>{translations.countPaidPauseInTotalWork}</label>
          <span>
            <Checkbox
              checked={formValues.countPaidPauseInTotalWork}
              onChange={handler('countPaidPauseInTotalWork')}
            />
          </span>
        </Field>
      </Card>

      <Card>
        <H3 className="mb-2">{translations.salaryCodeForActivityType}</H3>
        <Field>
          <label>{translations.driving}</label>
          <SalaryCodeSelect
            error={errors.drivingSalaryCode}
            fullWidth
            nullable
            value={formValues.drivingSalaryCode}
            onChange={handler('drivingSalaryCode')}
          />
        </Field>
        <Field>
          <label>{translations.otherWork}</label>
          <SalaryCodeSelect
            error={errors.otherWorkSalaryCode}
            fullWidth
            nullable
            value={formValues.otherWorkSalaryCode}
            onChange={handler('otherWorkSalaryCode')}
          />
        </Field>
        <Field>
          <label>{translations.availability}</label>
          <SalaryCodeSelect
            error={errors.availabilitySalaryCode}
            fullWidth
            nullable
            value={formValues.availabilitySalaryCode}
            onChange={handler('availabilitySalaryCode')}
          />
        </Field>
        <Field>
          <label>{translations.paidPauseSalaryCode}</label>
          <SalaryCodeSelect
            error={errors.paidPauseSalaryCode}
            fullWidth
            nullable
            value={formValues.paidPauseSalaryCode}
            onChange={handler('paidPauseSalaryCode')}
          />
        </Field>
      </Card>

      {(config.name === Module.TIMESHEETS ||
        config.name === Module.ACTIVITIES) && (
        <Card>
          <H3 className="mb-2">{translations.dataDistribution}</H3>
          {config.name === Module.ACTIVITIES && (
            <Field>
              <label>{translations.groupSalaryOnVehicle}</label>
              <span>
                <Checkbox
                  checked={formValues.groupSalaryOnVehicle}
                  onChange={handler('groupSalaryOnVehicle')}
                />
              </span>
            </Field>
          )}
          {config.name === Module.ACTIVITIES && (
            <>
              <Field>
                <label>{translations.useAutomaticDietGeneration}</label>
                <span>
                  <Checkbox
                    checked={formValues.useAutomaticDietGeneration}
                    onChange={handler('useAutomaticDietGeneration')}
                  />
                </span>
              </Field>
              <Field>
                <label>{translations.useAutomaticSalaryDayGeneration}</label>
                <span>
                  <Checkbox
                    checked={formValues.useAutomaticSalaryDayGeneration}
                    onChange={handler('useAutomaticSalaryDayGeneration')}
                  />
                </span>
              </Field>
              <Field>
                <label>{translations.holidayAllowances}</label>
                <SelectMultiple
                  fullWidth
                  selected={formValues.holidayAllowances}
                  options={holidayAllowanceSelectOptions}
                  onChange={handler('holidayAllowances')}
                />
              </Field>
            </>
          )}
          <Field>
            <label>{translations.usePeriodAddons}</label>
            <span>
              <Checkbox
                checked={formValues.usePeriodOvertime}
                onChange={handler('usePeriodOvertime')}
              />
            </span>
          </Field>
          {formValues.usePeriodOvertime && (
            <Field>
              {formValues.periodAddons.map((periodAddon, index) => (
                <PeriodAddonView
                  periodAddon={periodAddon}
                  onEdit={() => editPeriodAddon(periodAddon)}
                  onDelete={() => deletePeriodAddon(periodAddon)}
                  key={index}
                />
              ))}
              {isMobileOnly ? (
                <Button
                  height="32px"
                  padding="0 1rem"
                  onClick={() => addPeriodAddon()}
                >
                  <FAIcon icon="plus" $size="0.8rem" />
                </Button>
              ) : (
                <Button height="48px" onClick={() => addPeriodAddon()}>
                  {translations.add}
                </Button>
              )}
            </Field>
          )}
        </Card>
      )}
    </Form>
  )
}
